import { useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, ArrowUpRight, ArrowDownLeft, Send, QrCode, Wallet, RefreshCw } from 'lucide-react';
import { LoginArea } from '@/components/auth/LoginArea';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useBalance } from '@/hooks/useBalance';
import { useWalletMints } from '@/hooks/useWalletMints';
import { useDisplayUnit } from '@/hooks/useDisplayUnit';
import { useFiatPrice } from '@/hooks/useFiatPrice';
import { useUserPrivkeyHex } from '@/hooks/useUserPrivkey';
import { formatXmr, piconeroToXmr } from '@/lib/monero/units';
import { SUPPORTED_FIAT, formatFiat } from '@/lib/monero/fiat';
import { WarningBanner } from '@/components/shared/WarningBanner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/storage/db';
import type { TransactionRecord } from '@/lib/storage/db';
import { useToast } from '@/hooks/useToast';
import { useAppMode } from '@/contexts/ModeContext';
import { useAppContext } from '@/hooks/useAppContext';

import { StatusBar } from '@/components/shared/StatusBar';
import { DepositDialog } from '@/components/wallet/DepositDialog';
import { WithdrawDialog } from '@/components/wallet/WithdrawDialog';
import { SendEcashDialog } from '@/components/wallet/SendEcashDialog';
import { ReceiveEcashDialog } from '@/components/wallet/ReceiveEcashDialog';

function AddMintDialog({ onAdd }: { onAdd: (npub: string) => Promise<boolean> }) {
  const [npub, setNpub] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleAdd = async () => {
    if (!npub.trim()) return;
    setLoading(true);
    const success = await onAdd(npub.trim());
    setLoading(false);
    if (success) {
      toast({ title: 'Mint added', description: 'Mint has been added to your wallet.' });
      setNpub('');
    } else {
      toast({ title: 'Failed', description: 'Could not find a mint at this npub.', variant: 'destructive' });
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Plus className="h-4 w-4 mr-1" />
          Add Mint
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a Mint</DialogTitle>
          <DialogDescription>
            Enter the mint's Nostr public key (npub) to add it to your wallet.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label>Mint npub</Label>
            <Input
              placeholder="npub1..."
              value={npub}
              onChange={(e) => setNpub(e.target.value)}
            />
          </div>
          <Button onClick={handleAdd} disabled={loading || !npub.trim()}>
            {loading ? 'Fetching...' : 'Add Mint'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TransactionHistory() {
  const txs = useLiveQuery(
    () => db.walletHistory.orderBy('timestamp').reverse().limit(20).toArray(),
  );

  if (!txs || txs.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-8">
        No transactions yet
      </p>
    );
  }

  return (
    <ScrollArea className="h-[300px]">
      <div className="space-y-2">
        {txs.map((tx) => (
          <div key={tx.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50">
            <div className="flex items-center gap-3">
              {tx.type === 'deposit' && <ArrowDownLeft className="h-4 w-4 text-green-500" />}
              {tx.type === 'withdraw' && <ArrowUpRight className="h-4 w-4 text-red-500" />}
              {tx.type === 'send' && <Send className="h-4 w-4 text-orange-500" />}
              {tx.type === 'receive' && <ArrowDownLeft className="h-4 w-4 text-blue-500" />}
              <div>
                <p className="text-sm font-medium capitalize">{tx.type}</p>
                <p className="text-xs text-muted-foreground">
                  {new Date(tx.timestamp).toLocaleString()}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-mono">
                {tx.type === 'withdraw' || tx.type === 'send' ? '-' : '+'}
                {formatXmr(tx.amount, 6)} XMR
              </p>
              <Badge variant={tx.status === 'confirmed' ? 'default' : 'secondary'} className="text-[10px]">
                {tx.status}
              </Badge>
            </div>
          </div>
        ))}
      </div>
    </ScrollArea>
  );
}

export function WalletDashboard() {
  const { user } = useCurrentUser();
  const { mintBalances, totalBalance, loading: balanceLoading } = useBalance();
  const { mints, loading: mintsLoading, addMint, removeMint } = useWalletMints();
  const { unit, toggle, setFiat, isFiat, activeFiatCode } = useDisplayUnit();
  const { price: fiatPrice, loading: fiatLoading } = useFiatPrice(activeFiatCode ?? 'USD');
  const { setMode } = useAppMode();
  const userPrivkey = useUserPrivkeyHex();

  // Get proofs for each mint
  const allProofs = useLiveQuery(() => db.walletProofs.toArray());

  const getProofsForMint = useCallback((npub: string) => {
    return (allProofs ?? []).filter(p => p.mintNpub === npub);
  }, [allProofs]);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-sm w-full">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <Wallet className="h-12 w-12 text-primary" />
            </div>
            <CardTitle>Cashero Wallet</CardTitle>
            <CardDescription>
              Connect your Nostr identity to manage ecash
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <LoginArea className="w-full" />
            <p className="text-xs text-muted-foreground text-center">
              <WarningBanner />
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-bold">Cashero</h1>
            <Badge variant="outline" className="text-[10px]">Wallet</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setMode('mint')}>
              Switch to Mint
            </Button>
            <LoginArea className="w-auto" />
            <Button variant="ghost" size="sm" onClick={toggle}>
              {unit === 'xmr' ? 'piconero' : isFiat ? 'XMR' : `${activeFiatCode ?? 'USD'}`}
            </Button>
            {isFiat && (
              <select
                value={activeFiatCode ?? 'USD'}
                onChange={(e) => setFiat(e.target.value)}
                className="text-xs bg-muted rounded px-2 py-1 border"
              >
                {SUPPORTED_FIAT.map((f) => (
                  <option key={f.code} value={f.code}>{f.symbol} {f.code}</option>
                ))}
              </select>
            )}
          </div>
        </div>
      </header>
      <StatusBar />

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        <WarningBanner />

        {!userPrivkey && (
          <Card className="border-yellow-500/50 bg-yellow-500/10">
            <CardContent className="py-3">
              <p className="text-sm text-yellow-700 dark:text-yellow-400">
                ⚠️ Cashero requires login with nsec (private key) for ecash operations.
                Extension and bunker logins are not supported for deposits/withdrawals.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Balance */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground font-normal">Total Balance</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-bold font-mono tracking-tight">
              {balanceLoading ? '...' : formatXmr(totalBalance, 6)}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              XMR ({totalBalance.toLocaleString()} piconero)
            </p>
            {fiatPrice && (
              <p className="text-xs text-muted-foreground mt-2">
                ≈ {formatFiat(totalBalance, fiatPrice, SUPPORTED_FIAT.find(f => f.code === (activeFiatCode ?? 'USD'))?.symbol ?? '$')} {activeFiatCode ?? 'USD'}
              </p>
            )}
            {fiatLoading && (
              <p className="text-xs text-muted-foreground mt-2">Loading price...</p>
            )}
          </CardContent>
        </Card>

        {/* Mints + Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 space-y-4">
            <Tabs defaultValue="mints">
              <div className="flex items-center justify-between">
                <TabsList>
                  <TabsTrigger value="mints">Mints</TabsTrigger>
                  <TabsTrigger value="history">History</TabsTrigger>
                </TabsList>
                <AddMintDialog onAdd={addMint} />
              </div>

              <TabsContent value="mints" className="mt-4 space-y-3">
                {mintsLoading ? (
                  <p className="text-sm text-muted-foreground">Loading mints...</p>
                ) : mints.length === 0 ? (
                  <Card className="border-dashed">
                    <CardContent className="py-8 text-center">
                      <p className="text-sm text-muted-foreground">
                        No mints yet. Add a mint to get started.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  mints.map((mint) => {
                    const bal = mintBalances.find(b => b.npub === mint.npub);
                    const mintProofs = getProofsForMint(mint.npub);
                    return (
                      <Card key={mint.npub}>
                        <CardContent className="py-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{mint.name}</p>
                              <p className="text-xs text-muted-foreground font-mono">
                                {mint.npub.slice(0, 8)}...{mint.npub.slice(-8)}
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                Fees: {mint.fees.depositPercent}% deposit / {formatXmr(mint.fees.withdrawFlat, 6)} XMR withdraw
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-mono font-semibold">
                                {formatXmr(bal?.balance ?? 0, 4)}
                              </p>
                              <p className="text-xs text-muted-foreground">XMR</p>
                            </div>
                          </div>
                          <div className="flex gap-2 mt-3 flex-wrap">
                              {userPrivkey ? (
                              <>
                                <DepositDialog mint={mint} userPrivkey={userPrivkey} fiatPrice={fiatPrice} />
                                <WithdrawDialog mint={mint} proofs={mintProofs} userPrivkey={userPrivkey} fiatPrice={fiatPrice} />
                                <SendEcashDialog mint={mint} proofs={mintProofs} userPrivkey={userPrivkey} />
                                <ReceiveEcashDialog mint={mint} userPrivkey={userPrivkey} />
                              </>
                            ) : (
                              <>
                                <Button size="sm" variant="default" disabled title="Requires nsec login">Deposit</Button>
                                <Button size="sm" variant="secondary" disabled title="Requires nsec login">Withdraw</Button>
                                <Button size="sm" variant="outline" disabled title="Requires nsec login">Send</Button>
                                <Button size="sm" variant="outline" disabled title="Requires nsec login">Receive</Button>
                              </>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="ml-auto"
                              onClick={() => removeMint(mint.npub)}
                            >
                              Remove
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </TabsContent>

              <TabsContent value="history" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Transaction History</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <TransactionHistory />
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Quick Actions */}
          <div className="space-y-3">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {mints.length > 0 && userPrivkey ? (
                  <>
                    <DepositDialog mint={mints[0]} userPrivkey={userPrivkey} fiatPrice={fiatPrice} />
                    <WithdrawDialog mint={mints[0]} proofs={getProofsForMint(mints[0].npub)} userPrivkey={userPrivkey} fiatPrice={fiatPrice} />
                    <SendEcashDialog mint={mints[0]} proofs={getProofsForMint(mints[0].npub)} userPrivkey={userPrivkey} />
                    <ReceiveEcashDialog mint={mints[0]} userPrivkey={userPrivkey} />
                  </>
                ) : (
                  <>
                    <Button className="w-full" variant="default" disabled>Deposit</Button>
                    <Button className="w-full" variant="secondary" disabled>Withdraw</Button>
                    <Button className="w-full" variant="outline" disabled>Send Ecash</Button>
                    <Button className="w-full" variant="outline" disabled>Receive</Button>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">About</CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-muted-foreground space-y-2">
                <p>
                  Cashero is a Cashu-inspired ecash system using Monero (XMR) deposits and withdrawals,
                  communicating over Nostr.
                </p>
                <p>
                  <a href="https://shakespeare.diy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    Vibed with Shakespeare
                  </a>
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
